<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, type ComputedRef } from "vue";
import { useRouter, type Router } from "vue-router";
import type { AuthenticatedSession } from "../api/authentication.js";
import type { TenantSummary } from "../api/tenants.js";
import { useAuthenticationStore } from "../stores/authentication.js";
import { useTenantStore } from "../stores/tenants.js";
import { readSystemRole, type SystemRole } from "../api/system-authorization.js";

const authenticationStore: ReturnType<typeof useAuthenticationStore> = useAuthenticationStore();
const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const router: Router = useRouter();
const session: ComputedRef<AuthenticatedSession | null> = computed(
  (): AuthenticatedSession | null => authenticationStore.session,
);

async function selectTenant(event: Event): Promise<void> {
  const target: HTMLSelectElement = event.target as HTMLSelectElement;
  const userId: string | undefined = session.value?.user.id;
  if (userId === undefined) {
    return;
  }

  tenantStore.selectTenant(target.value, userId);
  await router.push({ name: "tenant-overview", params: { tenantId: target.value } });
}

function accessLabel(tenant: TenantSummary): string {
  const systemRole: SystemRole | null = readSystemRole(session.value?.user.systemRoles ?? []);
  if (systemRole !== null) {
    return systemRole.replace("system_", "System ").replace(/^./, (first: string): string => first.toUpperCase());
  }
  return (tenant.access.role ?? "No membership")
    .split("_")
    .map((part: string): string => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
</script>

<template>
  <label v-if="tenantStore.tenants.length > 0" class="tenant-switcher">
    <span>Tenant</span>
    <select :value="tenantStore.selectedTenantId ?? ''" aria-label="Active tenant" @change="selectTenant">
      <option v-for="tenant in tenantStore.tenants" :key="tenant.id" :value="tenant.id">
        {{ tenant.displayName }} · {{ accessLabel(tenant) }}
      </option>
    </select>
  </label>
</template>
